import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, format, isSameDay, isValid, parseISO, startOfWeek } from 'date-fns';
import { CalendarDays, FileText, Users } from 'lucide-react';
import { isSupabaseConfigured } from '@/lib/supabaseClient';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getQuotesThisMonth, calculateOverage, DEFAULT_PLAN_TIER, getQuoteCountByStatus } from '@/services/quoteService';
import { getClientCount, getTotalMonthlyRevenue } from '@/services/clientService';
import { getScheduledJobs } from '@/services/jobService';
import AppShell, { PageLoading } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import BillingBanner from '@/components/BillingBanner';
import OnboardingTour from '@/components/OnboardingTour';

const RECURRING_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  'bi-weekly': 'Bi-weekly',
  monthly: 'Monthly',
};

function safeParseDate(value) {
  if (!value || typeof value !== 'string') return null;

  try {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function safeFormatTime(value, fallback = 'Invalid time') {
  const parsed = safeParseDate(value);
  if (!parsed) return fallback;
  return format(parsed, 'p');
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Quotes this month state
  const [quotesThisMonth, setQuotesThisMonth] = useState(0);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [overageInfo, setOverageInfo] = useState(null);
  
  // Clients and revenue state
  const [clientCount, setClientCount] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [pendingQuotesCount, setPendingQuotesCount] = useState(0);

  // Weekly jobs snapshot (Mon-Sun)
  const [weeklyJobs, setWeeklyJobs] = useState([]);
  const [weeklyJobsLoading, setWeeklyJobsLoading] = useState(true);

  useEffect(() => {
    if (user && !loading) {
      loadAccountData();
    } else if (!loading && !user) {
      setAccountLoading(false);
    }
  }, [user, loading]);

  const loadAccountData = async () => {
    // Check if Supabase is configured
    if (!isSupabaseConfigured) {
      setError('Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.');
      setAccountLoading(false);
      setQuotesLoading(false);
      setWeeklyJobsLoading(false);
      return;
    }

    try {
      setAccountLoading(true);
      setQuotesLoading(true);
      setWeeklyJobsLoading(true);
      setError(null);
      
      console.log('[Dashboard] Loading account data for user:', user?.id);
      
      const result = await ensureUserAccount(user);
      
      console.log('[Dashboard] Account loaded:', result?.account);
      console.log('[Dashboard] Settings loaded:', result?.settings);
      
      setAccount(result?.account || null);
      
      // Load quotes count for this month
      if (result?.account?.id) {
        try {
          const count = await getQuotesThisMonth(result.account.id);
          setQuotesThisMonth(count);
          
          // Calculate overage info (using default plan tier for now)
          // TODO: Get plan_tier from account_settings when billing is integrated
          const planTier = result?.settings?.plan_tier || DEFAULT_PLAN_TIER;
          const overage = calculateOverage(count, planTier);
          setOverageInfo(overage);
          
          console.log('[Dashboard] Quotes this month:', count, 'Overage info:', overage);
        } catch (quoteErr) {
          console.error('[Dashboard] Error loading quotes count:', quoteErr);
          // Non-critical, don't show error but log it
          setQuotesThisMonth(0);
          setOverageInfo(null);
        }
        
        // Load clients count and revenue
        try {
          const [clients, revenue, pending] = await Promise.all([
            getClientCount(result.account.id),
            getTotalMonthlyRevenue(result.account.id),
            getQuoteCountByStatus(result.account.id, 'pending'),
          ]);
          setClientCount(clients);
          setMonthlyRevenue(revenue);
          setPendingQuotesCount(pending);
          console.log('[Dashboard] Clients:', clients, 'Revenue:', revenue, 'Pending:', pending);
        } catch (clientErr) {
          console.error('[Dashboard] Error loading client data:', clientErr);
          // Non-critical
          setClientCount(0);
          setMonthlyRevenue(0);
          setPendingQuotesCount(0);
        }

        // Load current week jobs (Mon-Sun)
        try {
          const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
          const weekEnd = addDays(weekStart, 7);
          const jobs = await getScheduledJobs(result.account.id, {
            startDate: weekStart.toISOString(),
            endDate: weekEnd.toISOString(),
          });
          setWeeklyJobs(jobs);
        } catch (jobsErr) {
          console.error('[Dashboard] Error loading weekly jobs:', jobsErr);
          setWeeklyJobs([]);
        }
      } else {
        setWeeklyJobs([]);
      }
    } catch (err) {
      // Create a clean error message from whatever error we received
      console.error('[Dashboard] Error loading account data:', err);
      console.error('[Dashboard] Error stack:', err?.stack);
      
      let errorMessage = 'Failed to load account data. ';
      
      // Safely extract error information
      if (err && typeof err === 'object') {
        if (err.message) {
          errorMessage += String(err.message);
        }
        if (err.code) {
          errorMessage += ` (Error code: ${String(err.code)})`;
        }
        if (err.hint) {
          errorMessage += ` Hint: ${String(err.hint)}`;
        }
      } else if (typeof err === 'string') {
        errorMessage += err;
      }
      
      setError(errorMessage);
    } finally {
      setAccountLoading(false);
      setQuotesLoading(false);
      setWeeklyJobsLoading(false);
    }
  };

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

  const jobsByWeekDay = weekDays.map((day) => {
    const items = weeklyJobs
      .filter((job) => {
        const start = safeParseDate(job?.start_at);
        return start ? isSameDay(start, day) : false;
      })
      .sort((a, b) => {
        const aTime = safeParseDate(a.start_at)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bTime = safeParseDate(b.start_at)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });

    return { day, items };
  });

  if (loading || accountLoading) {
    return <PageLoading />;
  }

  return (
    <AppShell
      title={`Welcome back${user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name}` : ''}`}
      description={account?.name || 'Manage quotes, jobs, and client follow-up from one workspace.'}
      accountName={account?.name}
      userLabel={user?.user_metadata?.full_name || user?.email}
      actions={
        <Button onClick={() => navigate('/quote')} className="bg-emerald-700 hover:bg-emerald-800">
          <FileText className="h-4 w-4" />
          Create Quote
        </Button>
      }
    >
      <OnboardingTour userId={user?.id} startWhen={!loading && !accountLoading && !error} />
      <BillingBanner />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card 
            className="cursor-pointer border-slate-200 shadow-sm transition-shadow hover:shadow-md"
            onClick={() => navigate('/clients')}
          >
            <CardHeader className="pb-2">
              <CardDescription>Active Clients</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold text-slate-950">{clientCount}</p>
              <p className="mt-1 text-sm text-slate-500">From won quotes</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardDescription>Quotes This Month</CardDescription>
            </CardHeader>
            <CardContent>
              {quotesLoading ? (
                <div className="animate-pulse">
                  <div className="h-9 w-16 rounded bg-slate-200"></div>
                </div>
              ) : (
                <>
                  <p className="text-3xl font-semibold text-slate-950">{quotesThisMonth}</p>
                  {overageInfo && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{overageInfo.planName} Plan</span>
                        <span>•</span>
                        <span>{overageInfo.includedLimit} included</span>
                      </div>
                      {overageInfo.isOverLimit && (
                        <p className="text-xs text-amber-700 mt-1">
                          {overageInfo.overageCount} overage quote{overageInfo.overageCount > 1 ? 's' : ''}
                        </p>
                      )}
                      {!overageInfo.isOverLimit && overageInfo.remainingIncluded > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {overageInfo.remainingIncluded} remaining this month
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardDescription>Estimated Monthly Revenue</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold text-slate-950">
                ${monthlyRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                From {clientCount} active client{clientCount !== 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Next Actions</CardTitle>
              <CardDescription>Move the business forward from the most common daily tasks.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <button
                type="button"
                onClick={() => navigate('/quote')}
                className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-left transition-colors hover:bg-emerald-100"
              >
                <FileText className="mt-0.5 h-5 w-5 text-emerald-800" />
                <span>
                  <span className="block font-medium text-emerald-950">Create a quote</span>
                  <span className="mt-1 block text-sm text-emerald-800">Price a new property and send it to a prospect.</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/quotes/pending')}
                className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4 text-left transition-colors hover:bg-slate-50"
              >
                <FileText className="mt-0.5 h-5 w-5 text-slate-600" />
                <span>
                  <span className="block font-medium text-slate-950">Follow up pending quotes</span>
                  <span className="mt-1 block text-sm text-slate-500">{pendingQuotesCount} open quote{pendingQuotesCount !== 1 ? 's' : ''} need a decision.</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/clients')}
                className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4 text-left transition-colors hover:bg-slate-50"
              >
                <Users className="mt-0.5 h-5 w-5 text-slate-600" />
                <span>
                  <span className="block font-medium text-slate-950">Review clients</span>
                  <span className="mt-1 block text-sm text-slate-500">Check active accounts and recurring revenue.</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/schedule')}
                className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4 text-left transition-colors hover:bg-slate-50"
              >
                <CalendarDays className="mt-0.5 h-5 w-5 text-slate-600" />
                <span>
                  <span className="block font-medium text-slate-950">Plan the schedule</span>
                  <span className="mt-1 block text-sm text-slate-500">See this week’s jobs and open service slots.</span>
                </span>
              </button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>This Week</CardTitle>
                  <CardDescription>
                    {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')} · Mon-Sun
                  </CardDescription>
                </div>
                <Button
                  onClick={() => navigate('/schedule')}
                  variant="outline"
                >
                  Open Calendar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {weeklyJobsLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
                  {jobsByWeekDay.map(({ day, items }) => (
                    <div key={day.toISOString()} className="min-h-28 rounded-md border border-slate-200 bg-white p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">{format(day, 'EEE')}</p>
                      <p className="text-sm font-semibold text-slate-950 mb-2">{format(day, 'MMM d')}</p>

                      {items.length === 0 ? (
                        <p className="text-xs text-slate-400">No jobs</p>
                      ) : (
                        <div className="space-y-2">
                          {items.slice(0, 3).map((job) => (
                            <div key={job.id} className="rounded border border-emerald-100 bg-emerald-50 px-2 py-1">
                              <p className="text-xs font-semibold text-emerald-950 truncate">{safeFormatTime(job.start_at)}</p>
                              <p className="text-xs text-slate-700 truncate">{job.client?.customer_name || 'Client'}</p>
                              <p className="text-xs text-slate-500 truncate">{job.service_type}</p>
                              {job.recurring_frequency && job.recurring_frequency !== 'none' && (
                                <p className="text-[10px] text-emerald-700 mt-0.5">{RECURRING_LABELS[job.recurring_frequency] || job.recurring_frequency}</p>
                              )}
                            </div>
                          ))}
                          {items.length > 3 && (
                            <p className="text-[11px] text-slate-500">+{items.length - 3} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
